// --- React ---
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// --- Services ---
import { useUnsavedChangesWarning } from "../../../hooks/useUnsavedChangesWarning";
import samplingScheduleSvc from "../../../services/api/samplingSchedules";

// --- Components ---
import ModalConfirm from "../../../components/modal-comfirm";
import Loading from "../../../components/loading/loading";
import FormInput from "../../../components/form-input/FormInput";
import FormDate from "../../../components/form-input/formDate";
import { notify } from "../../../common/notificationService";
import { omitMetaFields } from "../../../utils/helper";
import ButtonGoback from "../../../components/button/buttonGoback";

// --- Models ---
import { SamplingScheduleItem } from "../../../models/types/samplingSchedules";
import { samplingScheduleSchema } from "../../../models/schema/samplingSchedules";

// --- Utils ---
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { LoaderCircle } from "lucide-react";
import { UrlCollection } from "../../../common/url-collection";
import { useLookupParameterUnit } from "../../../utils/get-lookup";
import FormSelect from "../../../components/form-input/formSelect";

interface FormProps {
  isCreate: boolean;
  stationId: string | number;
  data: SamplingScheduleItem | null;
}

export default function ParameterCreateUpdate({ isCreate, stationId, data }: FormProps) {
  const navigate = useNavigate();
  const { lookupParameterUnit } = useLookupParameterUnit();

  const [loading, setLoading] = useState(false);

  const schema = samplingScheduleSchema;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      stationId: Number(stationId),
      scheduleType: '',
      intervalMinutes: 0,
      thresholdMin: 0,
      thresholdMax: 0,
      isActive: false,
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        ...omitMetaFields(data),
        stationId: Number(data.stationId),
      });
    }
  }, [data]);

  const onSubmit = async (values: any) => {
    console.log(values);
    setLoading(true);
    try {
      const res = isCreate ? await samplingScheduleSvc.createSamplingSchedule(values) : await samplingScheduleSvc.updateSamplingSchedule({ ...values, id: Number(data?.id) });
      if (res && res.content.status) {
        unlock();
        notify("Thành công", isCreate ? "Thêm thành công" : "Cập nhật thành công", "success");
        setLoading(false);
        navigate(UrlCollection.SamplingSchedule.replace(":stationId", stationId!.toString()));
      }
    } catch (e: any) {
      notify("Lỗi", "Xảy ra lỗi", "danger");
    } finally {
      setLoading(false);
    }
  };

  const { showModal, confirmLeave, cancelLeave, confirmContent, unlock } = useUnsavedChangesWarning(isDirty);

  return (
    <>
      <ModalConfirm
        isOpen={showModal}
        onClose={cancelLeave}
        title={confirmContent.title}
        content={confirmContent.content}
        titleComfirm="Rời trang"
        action={confirmLeave}
      />
      <div className="w-full h-full">
        <ButtonGoback />
        <div className="max-w-md mx-auto p-8 bg-white rounded-lg shadow mt-3">
          <h2 className="text-2xl font-bold mb-6 text-center">{isCreate ? "Thêm tham số" : "Cập nhật tham số"}</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-4">
              <FormInput
                label="Loại"
                name="scheduleType"
                type="text"
                register={register("scheduleType")}
                error={errors.scheduleType}
                required
              />
              <FormInput
                label="Khoảng thời gian (phút)"
                name="intervalMinutes"
                type="number"
                register={register("intervalMinutes")}
                error={errors.intervalMinutes}
                required
              />
              <FormDate
                label="Bắt đầu lấy mẫu"
                name="createdAt"
                control={control}
                outputFormat="YYYY-MM-DD"
                errors={errors}
                required
              />
              <FormDate
                label="Kế thúc lấy mẫu"
                name="lastRunAt"
                control={control}
                outputFormat="YYYY-MM-DD"
                errors={errors}
                required
              />
              <FormInput
                label="Ngưỡng tối thiểu"
                name="thresholdMin"
                type="decimal"
                register={register("thresholdMin")}
                error={errors.thresholdMin}
                required
              />
              <FormInput
                label="Ngưỡng tối đa"
                name="thresholdMax"
                type="decimal"
                register={register("thresholdMax")}
                error={errors.thresholdMax}
                required
              />
              <FormInput
                className="flex items-center gap-4"
                label="Trạng thái"
                name="isActive"
                type="checkbox"
                register={register("isActive")}
                error={errors.isActive}
                required
              />
            </div>
            <button
              disabled={!isDirty}
              type="submit"
              className="btn_submit w-full mt-4"
            >
              <div className="flex items-center justify-center gap-2">
                {loading ? <LoaderCircle /> : ""}
                {isCreate ? "Tạo mới" : "Cập nhật"}
              </div>
            </button>
          </form>
        </div>
      </div>
    </>
  );
}